<template>
  <div class="feedback-input-wrapper" :class="{ 'has-feedback': showFeedback }">
    <slot
      :on-input="handleInput"
      :on-change="handleChange"
      :on-focus="handleFocus"
      :on-blur="clearFieldFeedbackOnBlur"
      :field-name="fieldName"
      :error-messages="errorMessages"></slot>
    <FeedbackButton v-if="showFeedback" :show="showFeedback" @open="openDialog" />
    <Transition name="feedback-nudge">
      <div v-if="showNudge" role="status" class="feedback-nudge">
        <v-icon size="14" class="feedback-nudge-icon">mdi-message-alert-outline</v-icon>
        <span>You changed this answer</span>
        <button type="button" class="feedback-nudge-cta" @mousedown.prevent @click="openDialog">
          Tell us why to help us improve
        </button>
        <button
          type="button"
          class="feedback-nudge-dismiss"
          aria-label="Dismiss"
          @mousedown.prevent
          @click="dismissNudge">
          <v-icon size="13">mdi-close</v-icon>
        </button>
      </div>
    </Transition>
    <FeedbackDialog v-model="showDialog" :field-id="fieldId" @submit="handleSubmit" />
    <v-snackbar v-model="showError" color="error" timeout="3000">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useFeedback } from "@/composables/useFeedback";
import { analyticsServices } from "@/services/analyticsService";
import FeedbackButton from "./FeedbackButton.vue";
import FeedbackDialog from "./FeedbackDialog.vue";

const props = defineProps({
  fieldId: {
    type: String,
    required: true,
  },
  orderKey: {
    type: [String, null],
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, Array, Object, null],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  errorMessages: {
    type: [String, Array],
    default: null,
  },
  nudgeOnChange: {
    type: Boolean,
    default: false,
  },
  questionId: {
    type: String,
    default: null,
  },
});

const { markFieldAsEdited, clearFieldFeedback, isFieldEdited, getCurrentFieldId } = useFeedback();
const showDialog = ref(false);
const showError = ref(false);
const errorMessage = ref("");
const originalValue = ref(null);
const hasStartedEditing = ref(false);
const fieldName = ref(props.label);

// Watch for dialog close to clear field feedback
watch(showDialog, (newValue) => {
  if (!newValue) {
    clearFieldFeedback(props.fieldId);
  }
});

// Capture original value on mount
onMounted(() => {
  originalValue.value = props.modelValue;
});

// Reset original value when modelValue changes externally
watch(
  () => props.modelValue,
  (newValue) => {
    if (!hasStartedEditing.value) {
      originalValue.value = newValue;
    }
  }
);

const hasSavedOrder = computed(() => Boolean(props.orderKey));

const showFeedback = computed(() => {
  return hasSavedOrder.value && isFieldEdited(props.fieldId) && getCurrentFieldId() === props.fieldId;
});

const nudgeReady = ref(false);
const hasUserEdited = ref(false);
const dismissedForValue = ref(null);
const feedbackGivenForValue = ref(null);
let nudgeTimer = null;

const normalize = (value) => JSON.stringify(Array.isArray(value) ? [...value].sort() : value ?? null);

const isBlankValue = (value) => value == null || value === "" || (Array.isArray(value) && value.length === 0);

const hasChangedFromOriginal = computed(() => {
  return (
    hasUserEdited.value &&
    !isBlankValue(originalValue.value) &&
    normalize(props.modelValue) !== normalize(originalValue.value)
  );
});

const showNudge = computed(() => {
  return (
    props.nudgeOnChange &&
    hasSavedOrder.value &&
    nudgeReady.value &&
    hasChangedFromOriginal.value &&
    normalize(props.modelValue) !== dismissedForValue.value &&
    normalize(props.modelValue) !== feedbackGivenForValue.value
  );
});

watch(
  () => normalize(props.modelValue),
  () => {
    clearTimeout(nudgeTimer);
    if (hasChangedFromOriginal.value) {
      nudgeTimer = setTimeout(() => {
        nudgeReady.value = true;
      }, 600);
    } else {
      nudgeReady.value = false;
    }
  }
);

const dismissNudge = () => {
  dismissedForValue.value = normalize(props.modelValue);
};

onBeforeUnmount(() => {
  clearTimeout(nudgeTimer);
});

const clearFieldFeedbackOnBlur = (event) => {
  // Don't clear feedback if the click was on the feedback button
  if (!event.relatedTarget?.classList?.contains("feedback-button")) {
    clearFieldFeedback(props.fieldId);
  }
};

const handleInput = () => {
  if (!hasStartedEditing.value) {
    hasStartedEditing.value = true;
  }
  hasUserEdited.value = true;
  markFieldAsEdited(props.fieldId);
};

const handleChange = () => {
  if (!hasStartedEditing.value) {
    hasStartedEditing.value = true;
  }
  hasUserEdited.value = true;
  markFieldAsEdited(props.fieldId);
};

const handleFocus = () => {
  if (!hasStartedEditing.value) {
    hasStartedEditing.value = true;
  }
  markFieldAsEdited(props.fieldId);
};

// const handleBlur = () => {
//   // Don't clear the field feedback on blur anymore
//   // The button will stay visible until another field is focused
// };

const openDialog = () => {
  showDialog.value = true;
};

const serializeValue = (value) => (Array.isArray(value) ? value.join(", ") : value);

const handleSubmit = async (feedback) => {
  try {
    const feedbackData = {
      field_name: fieldName.value,
      original_value: serializeValue(originalValue.value),
      new_value: serializeValue(props.modelValue),
      feedback: feedback.feedback,
      ...(props.questionId ? { question_id: props.questionId } : {}),
    };

    await analyticsServices.submitFeedback(props.orderKey, feedbackData);
    feedbackGivenForValue.value = normalize(props.modelValue);
    showDialog.value = false;
    // Reset editing state but keep the original value
    hasStartedEditing.value = false;
    hasUserEdited.value = false;
    // Clear the field feedback after successful submission
    clearFieldFeedback(props.fieldId);
  } catch (error) {
    errorMessage.value = "Failed to submit feedback. Please try again.";
    showError.value = true;
  }
};
</script>

<style scoped>
.feedback-input-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.has-feedback {
  position: relative;
}

.has-feedback :deep(.v-input) {
  width: calc(100% - 40px);
}

.has-feedback :deep(.v-input__control) {
  width: 100%;
}

.feedback-nudge {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  margin: 2px 0 8px;
  padding: 3px 6px 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.06);
  border: 1px solid rgba(var(--v-theme-primary), 0.18);
}

.feedback-nudge-icon {
  opacity: 0.7;
}

.feedback-nudge-cta,
.feedback-nudge-dismiss {
  background: none;
  border: none;
  padding: 0;
}

.feedback-nudge-cta {
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.feedback-nudge-cta:hover {
  opacity: 0.75;
}

.feedback-nudge-dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  color: rgba(var(--v-theme-primary), 0.55);
  cursor: pointer;
}

.feedback-nudge-dismiss:hover {
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.feedback-nudge-enter-active,
.feedback-nudge-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.feedback-nudge-enter-from,
.feedback-nudge-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}
</style>
